import React, { useContext, useEffect, useRef, useState } from 'react';
import './index.less';
import classNames from 'classnames';
import { useDeepCompareEffect } from 'ahooks';
import { Button, Calendar, ConfigProvider, DatePicker, Form, Modal, Popover } from 'antd';
import { HeaderRender } from 'antd/es/calendar/generateCalendar';
import { CloseOutlined, LeftOutlined, RightOutlined } from '@ant-design/icons';
import dayjs, { Dayjs } from 'dayjs';
import 'dayjs/locale/zh-cn';
import dayLocaleData from 'dayjs/plugin/localeData';
import duration from 'dayjs/plugin/duration';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import { CalendarSetIcon, ExamDeleteIcon, ExamEditIcon } from '@/svgs';
import { deleteMyExamDate, getCalendarData, getStudentExamDate, setMyExamDate, updateMyExamDate } from '@/apis';
import { ExamDateItem, LiveStatus, CalendarTaskListItem, personalTrainingStatus, SmartSchoolSignStatus } from '@/types';
import SensorsDataContext from '@/components/home/context';
import ExamModal from './ExamModal';
import SignModal from './SignModal';
import { bindEvents } from '@/utils/sensors-data';
import { getBaseUrl } from '@/utils';

dayjs.extend(dayLocaleData);
dayjs.extend(duration);
dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);

interface IProps {
    projectName: string;
    projectId: number;
    subjects: {
        subjectId: number;
        subjectName: string;
    }[];
}

const CalendarSchedule = ({ projectName, projectId, subjects = [] }: IProps) => {
    const sensorsDataContextValues = useContext(SensorsDataContext);

    const [form] = Form.useForm();

    const [isExamModalOpen, setIsExamModalOpen] = useState(false);
    const [isSignModalOpen, setIsSignModalOpen] = useState(false);
    const [popoverVisible, setPopoverVisible] = useState(false); // 打开添加考试计划
    const [selectedDate, setSelectedDate] = useState<Dayjs>(dayjs());
    const [calendarDates, setCalendarDates] = useState<string[]>([]);
    const [tasksForSelectedDate, setTasksForSelectedDate] = useState<CalendarTaskListItem[]>([]);
    const [isSetButtonVisible, setSetButtonVisible] = useState(false);
    const [exams, setExams] = useState<ExamDateItem[]>([]);

    const calendarDataMap = useRef<{ [date: string]: CalendarTaskListItem[] }>({});
    const isOptionActive = useRef(false); // 记录一个值来控制弹窗操作
    const currentEditExamId = useRef(0); // 记录编辑id

    const noLiveImage = 'https://simg01.gaodunwangxiao.com/uploadfiles/tmp/upload/202407/09/87cba_20240709112946.png';
    const noLiveImg = 'https://simg01.gaodunwangxiao.com/uploadfiles/tmp/upload/202407/09/0fdf1_20240709135738.png';
    const personalTrainingIcon = 'https://simg01.gaodunwangxiao.com/uploadfiles/tmp/upload/202408/13/3109a_20240813154710.png';
    const smartSchoolIcon = 'https://simg01.gaodunwangxiao.com/uploadfiles/tmp/upload/202408/13/c373b_20240813154814.png';
    const liveIcon = 'https://simg01.gaodunwangxiao.com/uploadfiles/tmp/upload/202408/13/d634f_20240813154901.png';

    const fetchCalendarData = async () => {
        const initialSelectDate = dayjs();
        setSelectedDate(initialSelectDate);

        const response = await getCalendarData({
            startTime: initialSelectDate.subtract(1, 'month').startOf('month').format('YYYY-MM-DD'),
            endTime: initialSelectDate.add(1, 'month').endOf('month').format('YYYY-MM-DD'),
            forPc: true,
            trial: 1,
            projectId: projectId,
        });
        if (response.status !== 0 || !response.result) return;

        setCalendarDates(response.result.calendarList || []);
        setTasksForSelectedDate(response.result.liveSmartPersonalTaskList || []);
        calendarDataMap.current = { [initialSelectDate.format('YYYY-MM-DD')]: response.result.liveSmartPersonalTaskList || [] };
    };

    const fetchExamData = async () => {
        const res = await getStudentExamDate({ subjectIds: subjects.map(item => item.subjectId).join(',') });
        if (res.status === 0) {
            setExams(res.result.examDate || []);
            setTimeout(() => {
                setPopoverVisible(true);
            }, 500);
        }
    };

    const handleAddExam = () => {
        bindEvents('pc_studytab_calendar_addexamtime_click', {
            ...sensorsDataContextValues,
        });
        currentEditExamId.current = 0;
        setIsExamModalOpen(true);
        form.setFieldsValue({
            projectId,
            subjectId: undefined,
            examDate: undefined,
        });
    };

    const handleSaveExam = () => {
        form.validateFields().then(async res => {
            const params = {
                projectId,
                subjectId: res.subjectId,
                examDate: dayjs(res.examDate).format('YYYY-MM-DD'),
            };
            const ret = currentEditExamId.current ? await updateMyExamDate(currentEditExamId.current, params) : await setMyExamDate(params);
            if (ret.status === 0) {
                fetchExamData();
                setIsExamModalOpen(false);
                currentEditExamId.current = 0;
            }
        });
    };

    const handleDeleteExam = (id: number) => {
        isOptionActive.current = true;
        Modal.confirm({
            centered: true,
            getContainer: false,
            title: '温馨提示',
            content: '确定删除该科目的考试吗？删除后将无法查看该科目的倒计时及学习诊断',
            onCancel: () => (isOptionActive.current = false),
            onOk: async () => {
                const res = await deleteMyExamDate(id);
                if (res.status === 0) {
                    fetchExamData();
                    isOptionActive.current = false;
                }
            },
            footer: (_, { CancelBtn, OkBtn }) => (
                <ConfigProvider
                    theme={{
                        token: {
                            colorPrimary: '#rgba(255, 145, 62, 1)',
                            borderRadius: 8,
                        },
                    }}
                >
                    <CancelBtn />
                    <OkBtn />
                </ConfigProvider>
            ),
        });
    };

    const currentFetchId = useRef(0);
    const handleDateSelect = async (date: Dayjs) => {
        bindEvents('pc_studytab_calendar_click', { ...sensorsDataContextValues });
        setSelectedDate(date);
        setTasksForSelectedDate(calendarDataMap.current[date.format('YYYY-MM-DD')] || tasksForSelectedDate);

        if (!calendarDates.includes(date.format('YYYY-MM-DD'))) {
            setTasksForSelectedDate([]);
            return;
        }

        currentFetchId.current = currentFetchId.current++;
        const fetchId = currentFetchId.current;
        const response = await getCalendarData({
            startTime: date.format('YYYY-MM-DD'),
            endTime: date.format('YYYY-MM-DD'),
            isOneDay: 1,
            forPc: true,
            trial: 1,
            projectId: projectId,
        });
        if (fetchId !== currentFetchId.current) {
            calendarDataMap.current = {
                ...calendarDataMap.current,
                [date.format('YYYY-MM-DD')]: response.result.liveSmartPersonalTaskList,
            };
            return;
        }
        if (response.status !== 0 || !response.result) {
            setTasksForSelectedDate([]);
            return;
        }

        setTasksForSelectedDate(response.result.liveSmartPersonalTaskList || []);
        calendarDataMap.current = {
            ...calendarDataMap.current,
            [date.format('YYYY-MM-DD')]: response.result.liveSmartPersonalTaskList || [],
        };
    };

    const handleLiveClick = (task: CalendarTaskListItem['liveDto']) => {
        bindEvents('pc_studytab_calendar_resource_click', { ...sensorsDataContextValues });
        const { preLive, live, clipPlayback, onlinePlayback } = LiveStatus;
        const baseUrl = getBaseUrl();

        switch (task.resourceStatus) {
            case live:
            case preLive:
                window.open(task.ccLiveUrl);
                break;
            case onlinePlayback:
                window.open(task.recordUrl);
                break;
            case clipPlayback:
                window.open(
                    `${baseUrl !== '/' ? baseUrl : ''}/live-player/${task.courseId}/${task.resourceId}/${task.id}/${task.simplifyRecordUrl}`
                );
                break;
            default:
                break;
        }
    };

    const getLiveStatusText = (task: CalendarTaskListItem['liveDto']) => {
        const { resourceStatus } = task;
        const { notStart, notPlayback, preLive, live, clipPlayback, onlinePlayback } = LiveStatus;

        switch (resourceStatus) {
            case notStart:
                return '未开始';
            case preLive:
                return '教室已开启';
            case notPlayback:
                return '暂无回放';
            case live:
                return (
                    <>
                        直播中
                        <span className="live-icon" />
                    </>
                );
            case clipPlayback:
            case onlinePlayback:
                return '看回放';
            default:
                return null;
        }
    };

    const renderLiveTask = (task: CalendarTaskListItem['liveDto']) => {
        const { courseId, id, syllabusId, name, startTime, endTime, resourceStatus } = task;
        const { notStart, notPlayback } = LiveStatus;
        const isDisabled = resourceStatus === notStart || resourceStatus === notPlayback;
        const getKey = `${courseId}-${id}-${syllabusId}`;
        return (
            <div key={getKey} className="calendar-task-item">
                <div className="calendar-task-title">
                    <img className="calendar-task-icon" src={liveIcon} alt="" />
                    <div className="calendar-task-name">{name}</div>
                </div>
                <div className="calendar-task-flexbox">
                    <div className="calendar-task-time">
                        {dayjs(startTime).format('HH:mm')} - {dayjs(endTime).format('HH:mm')}
                    </div>
                    <div onClick={() => handleLiveClick(task)} className={classNames('space-default-btn', { disabled: isDisabled })}>
                        {getLiveStatusText(task)}
                    </div>
                </div>
            </div>
        );
    };

    const handlePersonalClick = (task: CalendarTaskListItem['personalTrainingDto']) => {
        bindEvents('pc_studytab_calendar_resource_click', { ...sensorsDataContextValues });

        if (task.isEnterStudio === personalTrainingStatus.ENTER) {
            window.open(task.enterStudioPath);
            return;
        }

        if (task.isEnterStudio === personalTrainingStatus.PLAYBACK && task.isPlayback) {
            window.open(task.enterPlaybackPath);
            return;
        }
    };

    const getPersonalTrainingStatusText = (task: CalendarTaskListItem['personalTrainingDto']) => {
        const { isEnterStudio, isPlayback } = task;
        const { NOT_START, BE_LATE, PLAYBACK, ENTER } = personalTrainingStatus;

        switch (isEnterStudio) {
            case NOT_START:
                return '未开始';
            case BE_LATE:
                return '迟到';
            case PLAYBACK:
                return isPlayback ? '看回放' : '暂无回放';
            case ENTER:
                return (
                    <>
                        去上课
                        <span className="live-icon" />
                    </>
                );
            default:
                return null;
        }
    };

    const renderPersonalTrainingTask = (task: CalendarTaskListItem['personalTrainingDto']) => {
        const { isEnterStudio, isPlayback, serviceType, id, subjectName, startTime, endTime } = task;
        const { NOT_START, BE_LATE, PLAYBACK } = personalTrainingStatus;
        const isLive = serviceType === 100529222;
        const isDisabled = isEnterStudio === NOT_START || isEnterStudio === BE_LATE || (isEnterStudio === PLAYBACK && !isPlayback);
        const taskName = subjectName ? `私教 | 你有一节私教课 | ${subjectName}` : '私教 | 你有一节私教课';
        return (
            <div key={id} className="calendar-task-item">
                <div className="calendar-task-title">
                    <img className="calendar-task-icon" src={personalTrainingIcon} alt="" />
                    <div className="calendar-task-name">{taskName}</div>
                    <div className="calendar-task-personal-type">{isLive ? '直播' : '电话'}</div>
                </div>
                <div className="calendar-task-flexbox">
                    <div className="calendar-task-time">
                        {dayjs(startTime).format('HH:mm')} - {dayjs(endTime).format('HH:mm')}
                    </div>
                    {isLive && (
                        <div
                            onClick={() => handlePersonalClick(task)}
                            className={classNames('space-default-btn', { disabled: isDisabled })}
                        >
                            {getPersonalTrainingStatusText(task)}
                        </div>
                    )}
                </div>
            </div>
        );
    };

    const handleSmartSchoolClick = (task: CalendarTaskListItem['smartSchoolDto']) => {
        bindEvents('pc_studytab_calendar_resource_click', { ...sensorsDataContextValues });

        const {
            courseId,
            id,
            signStatus,
            resource: { resourceId, startTime, endTime, playbackMode, playbackLink, liveUrlPlayBackPc },
        } = task;
        const currentTime = dayjs();
        const startBufferTime = dayjs(startTime).subtract(1, 'hours');

        if (
            currentTime.isSameOrAfter(startBufferTime) &&
            currentTime.isSameOrBefore(endTime) &&
            signStatus !== SmartSchoolSignStatus.sign
        ) {
            setIsSignModalOpen(true);
            return { statusText: '去签到', isDisabled: false };
        }

        if (dayjs().isAfter(endTime) && playbackMode === 0 && playbackLink) {
            const baseUrl = getBaseUrl();
            window.open(`${baseUrl !== '/' ? baseUrl : ''}/live-player/${courseId}/${resourceId}/${id}/${playbackLink}`);
            return;
        }

        if (dayjs().isAfter(endTime) && playbackMode === 1 && liveUrlPlayBackPc) {
            window.open(liveUrlPlayBackPc);
            return;
        }
    };

    const getSmartSchoolStatusText = (task: CalendarTaskListItem['smartSchoolDto']) => {
        const {
            signStatus,
            resource: { startTime, endTime, playbackMode, playbackLink, liveUrlPlayBackPc },
        } = task;
        const currentTime = dayjs();
        const startBufferTime = dayjs(startTime).subtract(1, 'hours');

        // 	在开始时间前60分钟
        if (currentTime.isBefore(startBufferTime)) {
            return { statusText: '暂未开始', isDisabled: true };
        }
        // 	面授开始前60分钟到结束时间段内，学员未签到
        if (
            currentTime.isSameOrAfter(startBufferTime) &&
            currentTime.isSameOrBefore(endTime) &&
            signStatus !== SmartSchoolSignStatus.sign
        ) {
            return { statusText: '去签到', isDisabled: false };
        }
        // 	学员已签到（包含补签）
        if (currentTime.isAfter(startTime)) {
            if ((playbackMode === 0 && playbackLink) || (playbackMode === 1 && liveUrlPlayBackPc)) {
                return { statusText: '看回放', isDisabled: false };
            }
            return { statusText: '暂无回放', isDisabled: true };
        }

        return { statusText: '', isDisabled: true };
    };

    const renderSmartSchoolTask = (task: CalendarTaskListItem['smartSchoolDto']) => {
        const {
            id,
            courseId,
            resource: { title, startTime, endTime },
        } = task;
        const { statusText, isDisabled } = getSmartSchoolStatusText(task);
        const getKey = `${courseId}-${id}`;
        return (
            <div key={getKey} className="calendar-task-item">
                <div className="calendar-task-title">
                    <img className="calendar-task-icon" src={smartSchoolIcon} alt="" />
                    <div className="calendar-task-name">{title}</div>
                </div>
                <div className="calendar-task-flexbox">
                    <div className="calendar-task-time">
                        {dayjs(startTime).format('HH:mm')} - {dayjs(endTime).format('HH:mm')}
                    </div>
                    {statusText && (
                        <div
                            onClick={() => handleSmartSchoolClick(task)}
                            className={classNames('space-default-btn', { disabled: isDisabled })}
                        >
                            {statusText}
                        </div>
                    )}
                </div>
            </div>
        );
    };

    const renderTask = (task: CalendarTaskListItem) => {
        switch (task.resourceType) {
            case 'live_new':
                return renderLiveTask(task.liveDto);
            case 'personal_training':
                return renderPersonalTrainingTask(task.personalTrainingDto);
            case 'smart_school':
                return renderSmartSchoolTask(task.smartSchoolDto);
            default:
                return null;
        }
    };

    const renderCalendarHeader: HeaderRender<Dayjs> = itemOption => {
        const { value, onChange } = itemOption;
        const year = value.year();
        const month = value.month();

        return (
            <div className="calendar-schedule-header">
                <div />
                <div className="time-info">
                    <LeftOutlined
                        onClick={() => {
                            const newValue =
                                month === 0
                                    ? value
                                          .clone()
                                          .year(year - 1)
                                          .month(11)
                                    : value.clone().month(month - 1);
                            onChange(newValue);
                        }}
                    />
                    <div>
                        {year}年{month + 1 > 9 ? month + 1 : '0' + (month + 1)}月
                    </div>
                    <RightOutlined
                        onClick={() => {
                            const newValue =
                                month === 11
                                    ? value
                                          .clone()
                                          .year(year + 1)
                                          .month(0)
                                    : value.clone().month(month + 1);
                            onChange(newValue);
                        }}
                    />
                </div>
                <Popover
                    placement="topRight"
                    content={
                        <div className="calendar-exam-list">
                            <CloseOutlined className="calendar-close-icon" onClick={() => setPopoverVisible(false)} />
                            {exams.length ? (
                                exams.map(item => (
                                    <div key={item.id} className="calendar-exam-item">
                                        <div className="project-subject">
                                            {item.projectName}-{item.subjectName}
                                        </div>
                                        <div className="option-box">
                                            <div>
                                                距离考试还有 <span>{+item.leftDays}</span> 天
                                            </div>
                                            <div>
                                                <ExamEditIcon
                                                    onClick={() => {
                                                        currentEditExamId.current = item.id;
                                                        setIsExamModalOpen(true);
                                                        form.setFieldsValue({
                                                            projectId,
                                                            subjectId: item.subjectId,
                                                            examDate: dayjs(item.examDate),
                                                        });
                                                    }}
                                                />
                                                <ExamDeleteIcon onClick={() => handleDeleteExam(item.id)} />
                                            </div>
                                        </div>
                                    </div>
                                ))
                            ) : (
                                <div className="no-exam-box">
                                    <img src={noLiveImage} />
                                    <div>未添加考试时间</div>
                                </div>
                            )}

                            <Button style={{ width: '100%' }} onClick={handleAddExam} type="primary">
                                添加考试时间
                            </Button>
                        </div>
                    }
                    overlayClassName="calendar-schedule-popover"
                    trigger="click"
                    open={popoverVisible}
                    onOpenChange={() => !isExamModalOpen && !isOptionActive.current && setPopoverVisible(false)}
                >
                    {isSetButtonVisible && (
                        <CalendarSetIcon
                            className={classNames('calendar-set-icon', { active: popoverVisible })}
                            onClick={() => setPopoverVisible(true)}
                        />
                    )}
                </Popover>
            </div>
        );
    };

    useEffect(() => {
        if (projectId) {
            fetchCalendarData();
        }
    }, [projectId]);

    // 用法与 useEffect 一致，但 deps 通过 react-fast-compare 进行深比较。
    useDeepCompareEffect(() => {
        if (subjects?.length) {
            fetchExamData();
        }
        setSetButtonVisible(subjects?.length ? true : false);
    }, [subjects]);

    return (
        <ConfigProvider
            theme={{
                token: {
                    colorPrimary: '#rgba(255, 145, 62, 1)',
                    borderRadius: 8,
                },
            }}
        >
            <div className="calendar-schedule-box">
                <Calendar
                    fullscreen={false}
                    headerRender={renderCalendarHeader}
                    fullCellRender={value => {
                        const isSelected = selectedDate && value.isSame(selectedDate, 'day');
                        const currentDate = dayjs(value).format('YYYY-MM-DD');
                        return (
                            <div className={isSelected ? 'custom-selected-date date-item' : 'date-item'}>
                                <div className="date-item-text">{value.date()}</div>
                                {calendarDates.includes(currentDate) && <div className="date-item-point" />}
                            </div>
                        );
                    }}
                    onSelect={handleDateSelect}
                />
                <div className="calendar-task-box">
                    {tasksForSelectedDate.length ? (
                        <>
                            <div className="calendar-selected-date">
                                <div>{dayjs(selectedDate).format('YYYY年MM月DD日')}</div>
                                <div>
                                    共 <span>{tasksForSelectedDate.length}</span> 个日程
                                </div>
                            </div>

                            <div className="calendar-task-list">{tasksForSelectedDate.map(task => renderTask(task))}</div>
                        </>
                    ) : (
                        <div className="no-calendar-task-list">
                            <img src={noLiveImg} />
                            <div>今日无日程</div>
                        </div>
                    )}
                </div>

                <ExamModal
                    isModalOpen={isExamModalOpen}
                    setIsModalOpen={setIsExamModalOpen}
                    form={form}
                    projectId={projectId}
                    projectName={projectName}
                    subjects={subjects}
                    handleSaveExam={handleSaveExam}
                    currentEditExamId={currentEditExamId}
                />
                <SignModal isModalOpen={isSignModalOpen} setIsModalOpen={setIsSignModalOpen} />
            </div>
        </ConfigProvider>
    );
};

export default CalendarSchedule;
